import React from "react";
import { Modal, Button, Form } from "@themesberg/react-bootstrap";

const CommonModal = ({ show, onClose, type, data }) => {
  // Common style for read-only fields (light gray background, flat borders)
  const inputStyle = {
    backgroundColor: "#f8f9fa", // light gray background for inputs
    border: "1px solid #ced4da",
    borderRadius: "0px",
    padding: "6px 12px",
  };

  // Style for a heading label
  const labelStyle = {
    fontWeight: 500,
    color: "#333",
    marginBottom: "4px",
  };

  // Style for sub-labels (for tags like Products, Categories, etc.)
  const subLabelStyle = {
    fontSize: "14px",
    color: "#6c757d",
    marginBottom: "4px",
  };

  // Style for tag items (flat, with light gray background)
  const tagStyle = {
    backgroundColor: "#e9ecef",
    border: "1px solid #ced4da",
    borderRadius: "0px",
    padding: "4px 8px",
    fontSize: "14px",
    color: "#333",
    marginRight: "8px",
    marginBottom: "8px",
  };

  const isQuestion = type === "question";
  const isTemplate = type === "template";

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      {/* Modal body: white background */}
      <Modal.Body style={{ padding: "24px", backgroundColor: "#ffffff" }}>
        {/* Header with title and close button */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            borderBottom: "1px solid #ced4da",
            paddingBottom: "8px",
          }}
        >
          <h4 style={{ fontWeight: 500, margin: 0, color: "#333" }}>
            {isTemplate
              ? "View Template Details"
              : "View Question Details"}
          </h4>
          <Button
            variant="light"
            onClick={onClose}
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              lineHeight: "20px",
              color: "#6c757d",
              border: "none",
            }}
          >
            &times;
          </Button>
        </div>

        {/* Section Heading */}
        <h6 style={{ fontWeight: 600, marginBottom: "8px", color: "#333" }}>
          {isTemplate ? "Template Details" : "Question Details"}
        </h6>

        {/* Common Field: Template Name or Question Text */}
        <div style={{ marginBottom: "16px" }}>
          <h6 style={labelStyle}>
            {isTemplate ? "Template Name" : "Question Text"}
          </h6>
          <Form.Control
            type="text"
            readOnly
            value={data?.text || ""}
            style={inputStyle}
          />
        </div>

        {/* ------------------ QUESTION SPECIFIC FIELDS ------------------ */}
        {isQuestion && (
          <>
            {/* Question Type */}
            <div style={{ marginBottom: "16px" }}>
              <h6 style={labelStyle}>Question Type</h6>
              <Form.Control
                type="text"
                readOnly
                value={data?.type || ""}
                style={inputStyle}
              />
            </div>

            {/* Answer Options */}
            <div style={{ marginBottom: "16px" }}>
              <h6 style={labelStyle}>Answer Options</h6>
              {data?.answerOptions && data.answerOptions.length > 0 ? (
                data.answerOptions.map((option, index) => (
                  <Form.Control
                    key={index}
                    type="text"
                    readOnly
                    value={option}
                    style={{ ...inputStyle, marginBottom: "8px" }}
                  />
                ))
              ) : (
                <Form.Control
                  type="text"
                  readOnly
                  value="No options provided"
                  style={inputStyle}
                />
              )}
            </div>

            {/* Assigned Template */}
            <div style={{ marginBottom: "16px" }}>
              <h6 style={labelStyle}>Assigned Template</h6>
              <Form.Control
                type="text"
                readOnly
                value={data?.template || ""}
                style={inputStyle}
              />
            </div>
          </>
        )}

        {/* ------------------ TEMPLATE SPECIFIC FIELDS ------------------ */}
        {isTemplate && (
          <>
            {/* Assigned Questions */}
            <div style={{ marginBottom: "16px" }}>
              <h6 style={labelStyle}>Assigned Questions</h6>
              {data?.questions && data.questions.length > 0 ? (
                data.questions.map((question, index) => (
                  <Form.Control
                    key={index}
                    type="text"
                    readOnly
                    value={question}
                    style={{ ...inputStyle, marginBottom: "8px" }}
                  />
                ))
              ) : (
                <Form.Control
                  type="text"
                  readOnly
                  value="No questions assigned"
                  style={inputStyle}
                />
              )}
            </div>

            {/* Exchange Value & Exchange Bonus */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div style={{ flex: 1 }}>
                <h6 style={labelStyle}>
                  Exchange Value <span style={{ color: "red" }}>*</span>
                </h6>
                <Form.Control
                  type="text"
                  readOnly
                  value={data?.exchangeValue || "₹ 0"}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <h6 style={labelStyle}>Exchange Bonus</h6>
                <Form.Control
                  type="text"
                  readOnly
                  value={data?.exchangeBonus || "₹ 0"}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Assign To Section */}
            <div style={{ marginBottom: "16px" }}>
              <h6 style={labelStyle}>Assign To</h6>
              {/* Products */}
              {data?.products && (
                <div style={{ marginBottom: "8px" }}>
                  <h6 style={subLabelStyle}>Products</h6>
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {data.products.map((product) => (
                      <div key={product} style={tagStyle}>
                        {product}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Categories */}
              {data?.categorys && (
                <div style={{ marginBottom: "8px" }}>
                  <h6 style={subLabelStyle}>Categories</h6>
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {data.categorys.map((category) => (
                      <div key={category} style={tagStyle}>
                        {category}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Brands */}
              {data?.brands && (
                <div>
                  <h6 style={subLabelStyle}>Brands</h6>
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {data.brands.map((brand) => (
                      <div key={brand} style={tagStyle}>
                        {brand}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CommonModal;
